export interface CreatureTraits {
  bodyShape: string;
  bodySize: number;
  appendages: string[];
  color: string;
  secondaryColor: string;
  pattern: string;
  specialAbilities: string[];
  metabolism: number;
  reproductionRate: number;
  complexity: number;
  letters: string[];
  eyeLetter: string;
  behaviorTraits: {
    speed: number;
    aggression: number;
    intelligence: number;
    sociability: number;
    adaptability: number;
  };
}

export class DNA {
  // New constants for DNA processing
  private static readonly VOWEL_SHAPES = ['circle', 'triangle', 'square', 'hexagon', 'star'];
  private static readonly CONSONANT_APPENDAGES: { [key: string]: string } = {
    'B': 'bristles', 'C': 'claws', 'F': 'fins', 'L': 'legs', 'P': 'spikes',
    'S': 'feelers', 'T': 'tentacles', 'W': 'wings', 'N': 'antennae', 'G': 'tail'
  };
  private static readonly PRIMARY_COLORS = ['#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF'];
  private static readonly SECONDARY_COLORS = ['#FF8800', '#88FF00', '#0088FF', '#8800FF', '#FF0088', '#00FF88'];
  private static readonly PUNCTUATION_ABILITIES: { [key: string]: string } = {
    '!': 'camouflage', '?': 'shield', '.': 'regeneration', ',': 'bioluminescence', ';': 'teleport'
  };

  constructor(private readonly sequence: string) {}

  public analyze(): CreatureTraits {
    // 1. Preprocessing
    const rawLetters = this.sequence.split('');
    const words = this.sequence.trim().split(/\s+/).filter(word => word.length > 0);

    // 2. Default Traits
    const traits: CreatureTraits = {
      bodyShape: 'circle',
      bodySize: 1.0,
      appendages: [],
      color: '#CCCCCC',
      secondaryColor: '#888888',
      pattern: 'solid',
      specialAbilities: [],
      metabolism: 1.0,
      reproductionRate: 1.0,
      complexity: this.sequence.length / 50,
      letters: this.sequence.toUpperCase().replace(/\s+/g, '').split(''),
      eyeLetter: 'V',
      behaviorTraits: {
        speed: 1.0,
        aggression: 1.0,
        intelligence: 1.0,
        sociability: 1.0,
        adaptability: 1.0
      }
    };

    // 3. Vowels for Body Shape and Size
    const vowels = rawLetters.filter(l => 'AEIOUaeiou'.includes(l));
    const vowelCount = vowels.length;
    const firstVowel = rawLetters.find(l => 'AEIOUaeiou'.includes(l));
    if (firstVowel) {
      traits.bodyShape = DNA.VOWEL_SHAPES[('AEIOUaeiou'.indexOf(firstVowel) % 5) % DNA.VOWEL_SHAPES.length];
    }
    traits.bodySize = 0.5 + (vowelCount * 0.1);
    traits.bodySize = Math.max(0.5, Math.min(traits.bodySize, 2.5));

    // 4. Consonants for Appendages
    rawLetters.forEach(letter => {
      const upperLetter = letter.toUpperCase();
      if (DNA.CONSONANT_APPENDAGES[upperLetter]) {
        if (traits.appendages.length < 5) {
          traits.appendages.push(DNA.CONSONANT_APPENDAGES[upperLetter]);
        }
      }
    });

    // 5. Capitalization for Color
    const capitalLetters = rawLetters.filter(l => l >= 'A' && l <= 'Z');
    const capitalCount = capitalLetters.length;
    if (capitalCount > 0) {
      traits.color = DNA.PRIMARY_COLORS[capitalCount % DNA.PRIMARY_COLORS.length];
      if (capitalCount > 1) {
        traits.secondaryColor = DNA.SECONDARY_COLORS[(capitalCount - 1) % DNA.SECONDARY_COLORS.length];
      } else {
        // Darken primary color slightly - simplistic approach
        const primaryColorValue = parseInt(traits.color.substring(1), 16);
        const darkerValue = Math.max(0, primaryColorValue - 0x202020).toString(16);
        traits.secondaryColor = `#${'000000'.substring(darkerValue.length)}${darkerValue}`;
      }
    }

    // 6. Punctuation for Special Abilities
    rawLetters.forEach(letter => {
      if (DNA.PUNCTUATION_ABILITIES[letter]) {
        if (traits.specialAbilities.length < 3 && !traits.specialAbilities.includes(DNA.PUNCTUATION_ABILITIES[letter])) {
          traits.specialAbilities.push(DNA.PUNCTUATION_ABILITIES[letter]);
        }
      }
    });
    
    // 7. Word Length (Average) for Metabolism
    if (words.length > 0) {
      const avgWordLength = words.reduce((sum, word) => sum + word.length, 0) / words.length;
      traits.metabolism = 0.5 + (avgWordLength * 0.1);
      traits.metabolism = Math.max(0.5, Math.min(traits.metabolism, 2.0));
    }

    // 8. Word Count for Reproduction Rate
    traits.reproductionRate = 0.5 + (words.length * 0.05);
    traits.reproductionRate = Math.max(0.5, Math.min(traits.reproductionRate, 2.0));
    
    // 9. Eye Letter & Behavior Traits (kept as defaults for now)
    // traits.eyeLetter and traits.behaviorTraits are already set with defaults.

    return traits;
  }

  // Note: calculateComplexity, determineBodyShape, determineBodySize, 
  // determineAppendages, determineColor, determineSecondaryColor, determinePattern, 
  // determineSpecialAbilities, determineMetabolism, determineReproductionRate, 
  // determineLetters, determineEyeLetter, determineBehaviorTraits, and hashSubstring
  // methods are removed as per instructions.

  public static mutate(dna: string, rate: number = 0.1): string {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const mutations = [
      // Replace random letter with new letter
      (s: string) => {
        if (s.length === 0) return s;
        const pos = Math.floor(Math.random() * s.length);
        return s.substring(0, pos) + letters[Math.floor(Math.random() * letters.length)] + s.substring(pos + 1);
      },
      // Add random letter
      (s: string) => s + letters[Math.floor(Math.random() * letters.length)],
      // Remove random letter
      (s: string) => {
        if (s.length === 0) return s;
        const pos = Math.floor(Math.random() * s.length);
        return s.substring(0, pos) + s.substring(pos + 1);
      },
      // Duplicate random letter
      (s: string) => {
        if (s.length === 0) return s;
        const pos = Math.floor(Math.random() * s.length);
        return s.substring(0, pos) + s[pos] + s.substring(pos);
      },
      // Swap adjacent letters
      (s: string) => {
        if (s.length < 2) return s;
        const pos = Math.floor(Math.random() * (s.length - 1));
        return s.substring(0, pos) + s[pos + 1] + s[pos] + s.substring(pos + 2);
      }
    ];

    // Apply random mutation if probability check passes
    if (Math.random() < rate) {
      const mutation = mutations[Math.floor(Math.random() * mutations.length)];
      return mutation(dna);
    }

    return dna;
  }
} 